import { createSelector } from '@ngrx/store';
import cloneDeep from 'lodash.clonedeep';
import { LEGACY_REPORT_TYPES } from 'CustomPDFReportsApp/ts/constants';
import { MODEL_PORTFOLIO, SECURITYLIST_GROUP_MULTI } from 'NewAngular/constants/core.constants';
import { CustomPDFReportNumRequiredSecurities } from 'CustomPDFReportsApp/ts/enums/custom-pdf-report-required-num-securities.enum';
import { CustomPDFReportType } from 'CustomPDFReportsApp/ts/enums/custom-pdf-report-type.enum';
import { CustomPDFReportModalEventName } from 'CustomPDFReportsApp/ts/features/custom-pdf-report-modal/enums/custom-pdf-report-modal-event-name.enum';
import { SavedScatterPlotHoldingsChoice } from 'CustomPDFReportsApp/ts/features/custom-pdf-report-modal/enums/custom-pdf-report-saved-asset-securities-choice.enum';
import { getSortedSections } from 'CustomPDFReportsApp/ts/utils/custom-pdf-report-utils';
import { isLegacyYRatingReport } from 'CustomPDFReportsApp/ts/utils/custom-pdf-reports-utils';
import { getSecurityTypeFromId } from 'NewAngular/utils/security-utils';
export const getReportMenuFetchRequest = (state) => state.reportMenuFetchRequest;
export const getMenuLoadingStatus = createSelector(getReportMenuFetchRequest, (fetchRequest) => fetchRequest.fetching);
// Report State
const getReportState = (state) => state;
export const getSelectedReport = createSelector(getReportState, (reportState) => reportState.selectedReport);
export const getCustomPDFReportParamsSections = createSelector(getReportState, (reportState) => reportState.sections);
export const getNumRequiredSecurities = createSelector(getReportState, (reportState) => {
    return reportState.selectedReport.num_required_securities;
});
export const getNamingConvention = createSelector(getReportState, (reportState) => reportState.namingConvention);
export const getPrimarySecurity = createSelector(getReportState, (reportState) => reportState.primarySecurity);
export const getSecondarySecurity = createSelector(getReportState, (reportState) => reportState.secondarySecurity);
export const getAllSecurities = createSelector(getReportState, (reportState) => {
    if (reportState.secondarySecurity.securityId) {
        return [reportState.primarySecurity, reportState.secondarySecurity, ...reportState.comparisonSecurities];
    }
    return [reportState.primarySecurity, ...reportState.comparisonSecurities];
});
export const getComparisonSecurities = createSelector(getReportState, (reportState) => reportState.comparisonSecurities);
export const getTalkingPoints = createSelector(getReportState, (reportState) => reportState.talkingPoints);
export const getCustomTexts = createSelector(getReportState, (reportState) => reportState.customTexts);
export const getTransitionAnalyses = createSelector(getReportState, (reportState) => reportState.transitionAnalyses);
export const getCurrentWizardStepIndex = createSelector(getReportState, (reportState) => reportState.currentWizardStepIndex);
export const getExportOption = createSelector(getReportState, (reportState) => reportState.exportOption);
export const getWizardSteps = createSelector(getSelectedReport, (report) => report.wizard_steps);
export const getSections = createSelector(getSelectedReport, (report) => {
    if ('sections' in report) {
        const customPDFReport = report;
        if (customPDFReport.sections.length && customPDFReport.sections[0].modules) {
            return getSortedSections(customPDFReport.sections);
        }
        return customPDFReport.sections;
    }
    return [];
});
export const getCurrentWizardStep = createSelector(getPrimarySecurity, getSecondarySecurity, getComparisonSecurities, getWizardSteps, getCurrentWizardStepIndex, (primarySecurity, secondarySecurity, comparisonSecurities, wizardSteps, stepIndex) => {
    if (wizardSteps === undefined || stepIndex === null || stepIndex > (wizardSteps ?? []).length - 1) {
        return null;
    }
    const step = cloneDeep(wizardSteps[stepIndex]);
    // Enable Model Portfolio-specific holdings options for scatter plots on the report if applicable
    if (step.eventName === CustomPDFReportModalEventName.SHOW_SECURITY_SEARCH_MODAL &&
        'scatter_plot_securities_choice' in (step.extraFields ?? {})) {
        let choicesToEnable = new Set();
        if (getSecurityTypeFromId(primarySecurity.securityId ?? '') === MODEL_PORTFOLIO) {
            choicesToEnable = new Set(Object.values(SavedScatterPlotHoldingsChoice));
        }
        else if ([secondarySecurity, ...comparisonSecurities].some((security) => getSecurityTypeFromId(security.securityId ?? '') === MODEL_PORTFOLIO)) {
            choicesToEnable = new Set([
                SavedScatterPlotHoldingsChoice.ADD_REPORT_SECURITIES_HOLDINGS,
                SavedScatterPlotHoldingsChoice.REPORT_SECURITIES_HOLDINGS_ONLY,
            ]);
        }
        step.extraFields.scatter_plot_securities_choice.choices =
            step.extraFields.scatter_plot_securities_choice.choices.map((choice) => {
                return { ...choice, disabled: choice.disabled && !choicesToEnable.has(choice.value) };
            });
    }
    return step;
});
export const getWizardStepEventName = createSelector(getCurrentWizardStepIndex, getWizardSteps, (currentWizardStepIndex, wizardSteps) => {
    if (wizardSteps === undefined || currentWizardStepIndex === null)
        return null;
    // if the index is equal to the length, this means we have finished all the wizard steps
    // and now need to generate the report
    if (currentWizardStepIndex === wizardSteps.length) {
        return CustomPDFReportModalEventName.GENERATE_CUSTOM_PDF_REPORT;
    }
    return wizardSteps[currentWizardStepIndex].eventName;
});
export const getIsLastStep = createSelector(getCurrentWizardStepIndex, getWizardSteps, (currentWizardStepIndex, wizardSteps) => {
    if (wizardSteps === undefined || currentWizardStepIndex === null)
        return null;
    return currentWizardStepIndex >= wizardSteps.length - 1;
});
export const getDefaultReportTitle = createSelector(getSelectedReport, getPrimarySecurity, getSecondarySecurity, (report, primarySecurity, secondarySecurity) => {
    const { report_name, num_required_securities } = report;
    if (primarySecurity.securityListGroup === SECURITYLIST_GROUP_MULTI) {
        return report_name;
    }
    switch (num_required_securities) {
        case CustomPDFReportNumRequiredSecurities.ONE:
            return `${primarySecurity.securityName} ${report_name}`;
        case CustomPDFReportNumRequiredSecurities.TWO:
            return `${primarySecurity.securityName} vs. ${secondarySecurity.securityName} ${report_name}`;
        case CustomPDFReportNumRequiredSecurities.THREE_PLUS:
            return `${primarySecurity.securityName} vs. Others ${report_name}`;
        default:
            return report_name;
    }
});
export const getCoverPageInfo = createSelector(getReportState, (reportState) => reportState.coverPageInfo);
export const getUserPreferenceFetchRequest = createSelector(getReportState, (reportState) => reportState.userPreferenceFetchRequest);
export const getCoverPageLoadingStatus = createSelector(getUserPreferenceFetchRequest, (fetchRequest) => fetchRequest.fetching);
export const getCoverPageErrorStatus = createSelector(getUserPreferenceFetchRequest, (fetchRequest) => fetchRequest.error);
export const getCustomPdfReportMenu = createSelector(getReportState, (reportState) => {
    return reportState.customPdfReportMenu;
});
export const getCurrentReportTypeIndex = createSelector(getReportState, (reportState) => reportState.currentReportTypeIndex);
const getCurrentReportType = createSelector(getReportState, (reportState) => {
    const reportMenu = reportState.customPdfReportMenu;
    if (!!reportMenu && reportMenu.length > 0) {
        return reportMenu[reportState.currentReportTypeIndex];
    }
    return null;
});
export const getCurrentReportTypeName = createSelector(getCurrentReportType, (reportType) => {
    return reportType !== null ? reportType.name : '';
});
export const getReportsForReportType = createSelector(getCurrentReportType, (reportType) => {
    if (reportType !== null) {
        return reportType.reports;
    }
    return [];
});
export const getCurrentReportIndex = createSelector(getReportState, (reportState) => reportState.currentReportIndex);
export const getGeneratingReport = createSelector(getReportState, (reportState) => reportState.generatingSavedReport);
export const getGeneratingUnsavedReport = createSelector(getReportState, (reportState) => reportState.generateReportConfig);
export const getGenerationErrors = createSelector(getReportState, (reportState) => reportState.generationErrors);
export const getTearsheetsReportUrl = createSelector(getReportState, (reportState) => {
    const report = reportState.selectedReport;
    return 'tearsheets_report_url' in report ? report.tearsheets_report_url : '';
});
export const getYRatingReportUrl = createSelector(getReportState, (reportState) => {
    const report = reportState.selectedReport;
    if (isLegacyYRatingReport(report)) {
        return report.y_rating_report_url;
    }
    return '';
});
export const getReportTitle = createSelector(getReportState, (reportState) => reportState.reportTitle);
export const getSavedAssetSecuritiesSelections = createSelector(getReportState, (reportState) => {
    return {
        fund_chart_securities_choice: reportState.fundChartSecuritiesChoice,
        scatter_plot_securities_choice: reportState.scatterPlotSecuritiesChoice,
        comp_table_securities_choice: reportState.compTableSecuritiesChoice,
        timeseries_table_securities_choice: reportState.timeseriesTableSecuritiesChoice,
    };
});
export const getSavedHypotheticalId = createSelector(getReportState, (reportState) => reportState.savedHypotheticalId);
export const getSavedAnalysisPlanId = createSelector(getReportState, (reportState) => reportState.savedAnalysisPlanId);
export const getRiskProfile = createSelector(getReportState, (reportState) => reportState.riskProfile);
export const getSecurityLevelValues = createSelector(getReportState, (reportState) => reportState.securityLevelValues);
export const generatePostParams = createSelector(getSelectedReport, getPrimarySecurity, getAllSecurities, getReportTitle, getCoverPageInfo, getSavedAssetSecuritiesSelections, getSavedHypotheticalId, getSavedAnalysisPlanId, getRiskProfile, getTalkingPoints, getCustomTexts, getTransitionAnalyses, getNamingConvention, getSecurityLevelValues, getExportOption, getCustomPDFReportParamsSections, (report, primarySecurity, allSecurities, reportTitle, coverPageInfo, savedAssetSecuritiesSelections, savedHypotheticalId, savedAnalysisPlanId, riskProfile, talkingPoints, customTexts, transitionAnalyses, namingConvention, securityLevelValues, exportOption, sections) => {
    const { report_type } = report;
    const securityIds = allSecurities
        .filter((security) => security.securityId)
        .map((security) => security.securityId);
    const customLabels = allSecurities
        .filter((security) => security.customLabel)
        .map((security) => security.customLabel);
    switch (report_type) {
        case CustomPDFReportType.AGGREGATE_SECURITY_COMPARISON:
        case CustomPDFReportType.AGGREGATE_SECURITY_OVERVIEW:
        case CustomPDFReportType.MULTI_SECURITY_COMPARISON:
            return {
                security_ids: securityIds,
                contact_email: coverPageInfo.contactEmail,
                contact_phone: coverPageInfo.contactPhone,
                contact_url: coverPageInfo.contactUrl,
                organization: coverPageInfo.organization,
                prepared_by: coverPageInfo.preparedBy,
                prepared_for: coverPageInfo.preparedFor,
                custom_field: coverPageInfo.customField,
                report_title: reportTitle,
                as_of_date_option: coverPageInfo.asOfDateOption,
                designation_type: coverPageInfo.designationType,
                saved_hypothetical_id: savedHypotheticalId,
                saved_analysis_plan_id: savedAnalysisPlanId,
                risk_profile: riskProfile,
                talking_points: talkingPoints,
                custom_texts: customTexts,
                serialized_transition_analyses: transitionAnalyses,
                naming_convention: namingConvention,
                dollar_conversion_levels: securityLevelValues,
                custom_security_labels: customLabels,
                securitylist_group: primarySecurity.securityListGroup,
                export_type: exportOption,
                performance_disclosure_footnote: report.performance_disclosure_footnote,
                sections,
                ...savedAssetSecuritiesSelections,
            };
        case LEGACY_REPORT_TYPES.TEARSHEET:
            return {
                as_of_date_option: coverPageInfo.asOfDateOption,
                report_type: report.tearsheet_type,
                export_type: exportOption,
            };
        default:
            return {};
    }
});
